// src/services/neighborhoodService.js
import { supabase } from './supabase';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || 'https://kcwaqseendhxbsnpmlkg.supabase.co';
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;
const STORAGE_BUCKET = 'property-images';
const FALLBACK_STORAGE_KEY = 'nobrokerage_neighborhoods_fallback';

export const INITIAL_NEIGHBORHOODS = [
  {
    id: 'bbc09e52-e192-4a44-81b8-85f136a22721',
    name: 'Assi Ghat',
    city: 'Varanasi, UP',
    image_url: '/images/neighborhoods/assi-ghat.jpg',
    price_trend: '+15.1%',
    source_note: 'Indicative (Circle Rate Trend)',
    priority: 1,
    status: 'Active',
    created_at: new Date().toISOString()
  },
  {
    id: '8b8ea352-ffc7-4bfb-bb75-9d4f1b309f05',
    name: 'Gomti Nagar',
    city: 'Lucknow, UP',
    image_url: '/images/neighborhoods/gomti-nagar.jpg',
    price_trend: '+12.5%',
    source_note: 'Indicative (Market Survey)',
    priority: 2,
    status: 'Active',
    created_at: new Date().toISOString()
  },
  {
    id: '86a986a4-7110-4df1-a610-619bb6891d76',
    name: 'Mahua Bagh',
    city: 'Ghazipur, UP',
    image_url: '/images/neighborhoods/mahua-bagh.jpg',
    price_trend: '+4.8%',
    source_note: 'Indicative (Registry Index)',
    priority: 3,
    status: 'Active',
    created_at: new Date().toISOString()
  }
];

const getLocalFallback = () => {
  try {
    const raw = localStorage.getItem(FALLBACK_STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) {
    console.error('Error reading neighborhood fallback storage:', e);
  }
  return INITIAL_NEIGHBORHOODS;
};

const setLocalFallback = (list) => {
  try {
    localStorage.setItem(FALLBACK_STORAGE_KEY, JSON.stringify(list));
  } catch (e) {
    console.error('Error saving neighborhood fallback storage:', e);
  }
};

/**
 * Resizes and compresses an image in-browser using HTML5 Canvas.
 * Caps maximum width at 1200px while preserving aspect ratio.
 */
export const compressImage = (file, maxWidth = 1200, quality = 0.85) => {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) {
      return reject(new Error('Selected file must be an image.'));
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let width = img.width;
        let height = img.height;

        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(new File([blob], file.name.replace(/\.[^/.]+$/, '.jpg'), { type: 'image/jpeg' }));
            } else {
              resolve(file); // fallback to original if canvas fails
            }
          },
          'image/jpeg',
          quality
        );
      };
      img.onerror = () => reject(new Error('Failed to load image for compression.'));
      img.src = e.target.result;
    };
    reader.onerror = () => reject(new Error('Failed to read image file.'));
    reader.readAsDataURL(file);
  });
};

/**
 * Uploads a compressed neighborhood image to Supabase Storage.
 */
export const uploadNeighborhoodImage = async (file) => {
  const compressed = await compressImage(file, 1200, 0.85);
  const ext = 'jpg';
  const randomHex = Math.random().toString(36).substring(2, 9);
  const fileName = `neighborhood_${Date.now()}_${randomHex}.${ext}`;

  const { error } = await supabase.storage
    .from(STORAGE_BUCKET)
    .upload(fileName, compressed, { contentType: 'image/jpeg', upsert: true });

  if (error) {
    console.warn('Supabase storage upload notice:', error.message);
    // Fallback: return data URL for local display
    return new Promise((res) => {
      const r = new FileReader();
      r.onloadend = () => res(r.result);
      r.readAsDataURL(compressed);
    });
  }

  const { data } = supabase.storage.from(STORAGE_BUCKET).getPublicUrl(fileName);
  return data.publicUrl;
};

export const neighborhoodService = {
  /**
   * Fetch all neighborhoods
   */
  async getNeighborhoods() {
    try {
      const { data, error } = await supabase
        .from('neighborhoods')
        .select('*')
        .order('priority', { ascending: true })
        .order('created_at', { ascending: true });

      if (error || !data || data.length === 0) {
        return { data: getLocalFallback(), isFallback: true };
      }

      return { data, isFallback: false };
    } catch (err) {
      console.warn('Fallback to local neighborhoods due to error:', err);
      return { data: getLocalFallback(), isFallback: true };
    }
  },

  /**
   * Create new neighborhood
   */
  async createNeighborhood(hoodData) {
    const payload = {
      name: hoodData.name.trim(),
      city: hoodData.city.trim(),
      image_url: hoodData.image_url.trim(),
      price_trend: hoodData.price_trend ? hoodData.price_trend.trim() : null,
      source_note: hoodData.source_note ? hoodData.source_note.trim() : 'Indicative',
      priority: parseInt(hoodData.priority, 10) || 1,
      status: hoodData.status || 'Active'
    };

    try {
      const { data, error } = await supabase
        .from('neighborhoods')
        .insert([payload])
        .select()
        .single();

      if (error) {
        const list = getLocalFallback();
        const newRecord = {
          ...payload,
          id: `local-${Date.now()}`,
          created_at: new Date().toISOString()
        };
        list.push(newRecord);
        setLocalFallback(list);
        return { data: newRecord, error: null, isFallback: true };
      }

      return { data, error: null };
    } catch (err) {
      return { data: null, error: err };
    }
  },

  /**
   * Update neighborhood
   */
  async updateNeighborhood(id, hoodData) {
    const payload = {
      name: hoodData.name.trim(),
      city: hoodData.city.trim(),
      image_url: hoodData.image_url.trim(),
      price_trend: hoodData.price_trend ? hoodData.price_trend.trim() : null,
      source_note: hoodData.source_note ? hoodData.source_note.trim() : 'Indicative',
      priority: parseInt(hoodData.priority, 10) || 1,
      status: hoodData.status,
      updated_at: new Date().toISOString()
    };

    try {
      const { data, error } = await supabase
        .from('neighborhoods')
        .update(payload)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        const list = getLocalFallback().map((item) =>
          item.id === id ? { ...item, ...payload } : item
        );
        setLocalFallback(list);
        return { data: payload, error: null, isFallback: true };
      }

      return { data, error: null };
    } catch (err) {
      return { data: null, error: err };
    }
  },

  /**
   * Delete neighborhood
   */
  async deleteNeighborhood(id) {
    try {
      const { error } = await supabase
        .from('neighborhoods')
        .delete()
        .eq('id', id);

      if (error) {
        const list = getLocalFallback().filter((item) => item.id !== id);
        setLocalFallback(list);
      }

      return { success: true };
    } catch (err) {
      return { success: false, error: err };
    }
  },

  /**
   * Toggle Active / Inactive
   */
  async toggleStatus(id, currentStatus) {
    const nextStatus = currentStatus === 'Active' ? 'Inactive' : 'Active';
    return this.updateNeighborhood(id, { status: nextStatus });
  },

  /**
   * Reorder priorities
   */
  async updatePriorities(orderedList) {
    try {
      for (let i = 0; i < orderedList.length; i++) {
        const item = orderedList[i];
        await supabase
          .from('neighborhoods')
          .update({ priority: i + 1 })
          .eq('id', item.id);
      }
      return { success: true };
    } catch (e) {
      console.warn('Neighborhood priority update error:', e);
      return { success: false, error: e };
    }
  }
};
